import React from 'react';
import useRentalWizard from '../../../hooks/useRentalWizard';

/**
 * Premium Rental Wizard Component.
 * Guides the user through a 7-step process to create a new rental contract.
 */
export default function NewRentalForm({ onCancel, onSuccess }) {
    const {
        step, setStep,
        stepTitles,
        availableItems,
        customers,
        allRentals,
        loading,
        submitting,
        error, setError,
        rental, setRental,
        rentalItems, setRentalItems,
        showCustomerAdd, setShowCustomerAdd,
        newCustomer, setNewCustomer,
        handleAddInventoryItem,
        handleAddManualItem,
        updateItem,
        calculateItemTotals,
        calculateGrandTotals,
        handleCreate,
        handleAddCustomer
    } = useRentalWizard({ onSuccess });

    const currentCustomer = customers.find(c => c.id === rental.customer_id);

    const checkAvailability = () => {
        if (!rental.start_date || rentalItems.length === 0) return true;
        const start = new Date(rental.start_date);
        const end = rental.end_date ? new Date(rental.end_date) : start;
        start.setHours(0,0,0,0);
        end.setHours(23,59,59,999);

        for (const ex of allRentals) {
            if (ex.status === 'completed' || ex.status === 'cancelled') continue;
            const exStart = new Date(ex.start_date);
            const exEnd = new Date(ex.end_date);
            exStart.setHours(0,0,0,0);
            exEnd.setHours(23,59,59,999);
            
            if (start <= exEnd && end >= exStart) {
                const hasItem = ex.line_items?.some(li => rentalItems.some(ri => ri.id === li.rental_item_id));
                if (hasItem) return false;
            }
        }
        return true;
    };
    
    const isAvailable = checkAvailability();

    if (loading) return <div className="rental-loading">Prozesse werden geladen...</div>;

    const currentTitle = stepTitles[step - 1];

    return (
        <div className="new-rental-wizard" style={{ animation: 'fadeInScale 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>
            <div className="wizard-main premium-card" style={{ maxWidth: '800px', margin: '0 auto' }}>
                
                {/* Header with Navigation & Progress */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', alignItems: 'center', borderBottom: '1px solid var(--p-slate-100)', paddingBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <button className="back-button" style={{ margin: 0 }} onClick={onCancel}>
                            ← ABBRECHEN
                        </button>
                        <div>
                            <div style={{ fontSize: '0.6rem', fontWeight: '900', color: 'var(--p-slate-400)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>SCHRITT {step} / 7</div>
                            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '900', color: 'var(--p-slate-900)' }}>{currentTitle}</h2>
                        </div>
                    </div>
                    
                    {/* Status Tracker */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                        {[1, 2, 3, 4, 5, 6, 7].map(s => (
                            <div key={s} style={{ 
                                width: '30px', 
                                height: '4px', 
                                borderRadius: '2px', 
                                background: s <= step ? 'var(--optimox-blue)' : 'var(--p-slate-100)',
                                transition: 'all 0.3s ease'
                            }} />
                        ))}
                    </div>
                </div>

                {error && (
                    <div style={{ padding: '12px 20px', background: '#fff1f2', color: '#e11d48', borderRadius: '12px', marginBottom: '24px', fontSize: '0.8rem', fontWeight: '700', border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>⚠️</span> {error}
                    </div>
                )}

                {/* STEP 1: VORGANG & BEREICH (MANDATORY ID) */}
                {step === 1 && (
                    <div className="wizard-step">
                        <div style={{ background: 'var(--p-slate-50)', padding: '24px', borderRadius: '16px', marginBottom: '24px' }}>
                            <div className="form-group-compact" style={{ marginBottom: '24px' }}>
                                <label style={{ fontSize: '0.75rem', fontWeight: '900', color: 'var(--p-slate-700)', marginBottom: '8px', display: 'block' }}>1. VORGANGSNUMMER / INTERNE ID *</label>
                                <input 
                                    type="text" 
                                    placeholder="z.B. V-2024-001"
                                    value={rental.rental_number} 
                                    onChange={e => setRental({...rental, rental_number: e.target.value.toUpperCase()})}
                                    style={{ 
                                        width: '100%', 
                                        padding: '16px', 
                                        fontSize: '1.2rem', 
                                        fontWeight: '900', 
                                        borderRadius: '12px', 
                                        border: '2px solid var(--p-slate-200)',
                                        color: 'var(--optimox-blue)',
                                        background: '#fff',
                                        letterSpacing: '1px'
                                    }}
                                />
                                <p style={{ fontSize: '0.65rem', color: 'var(--p-slate-400)', marginTop: '8px', fontWeight: '600' }}>Diese Nummer identifiziert den gesamten Vermietungsvorgang kaufmännisch.</p>
                            </div>

                            <label style={{ fontSize: '0.75rem', fontWeight: '900', color: 'var(--p-slate-700)', marginBottom: '12px', display: 'block' }}>2. KATEGORIE WÄHLEN</label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                {[
                                    { id: 'vehicle', icon: '🚜', label: 'Fahrzeuge' },
                                    { id: 'material', icon: '📦', label: 'Material' },
                                    { id: 'other', icon: '🛠️', label: 'Sonstiges' }
                                ].map(cat => (
                                    <button key={cat.id} className={`item-card-compact ${rental.category === cat.id ? 'active' : ''}`}
                                        style={{ cursor: 'pointer', textAlign: 'center', padding: '30px 12px', border: rental.category === cat.id ? '2px solid var(--optimox-blue)' : '1px solid var(--p-slate-200)' }}
                                        onClick={() => setRental({...rental, category: cat.id})}>
                                        <span style={{ fontSize: '2rem', display: 'block', marginBottom: '10px' }}>{cat.icon}</span>
                                        <strong style={{ fontSize: '0.85rem' }}>{cat.label}</strong>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <button className="rental-btn-primary large" style={{ width: '100%', padding: '18px' }} disabled={!rental.rental_number} onClick={() => setStep(2)}>
                            WEITER ZUR OBJEKTAUSWAHL ➔
                        </button>
                    </div>
                )}

                {/* STEP 2: ITEM SELECTION */}
                {step === 2 && (
                    <div className="wizard-step">
                        <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', padding: '4px' }}>
                            {availableItems.filter(i => i.category === rental.category).map(item => {
                                const isSelected = rentalItems.some(si => si.id === item.id);
                                return (
                                <div key={item.id} 
                                    className="item-card-compact" 
                                    style={{ 
                                        cursor: item.active ? 'pointer' : 'not-allowed', 
                                        opacity: item.active || isSelected ? 1 : 0.6,
                                        border: isSelected ? '2px solid var(--optimox-blue)' : '1px solid var(--p-slate-200)',
                                        background: isSelected ? 'var(--p-blue-50)' : '#fff'
                                    }} 
                                    onClick={() => item.active && handleAddInventoryItem(item)}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <strong style={{ fontSize: '0.9rem' }}>{item.name}</strong>
                                        <span className={`status-pill-tiny ${item.active ? 'active' : 'busy'}`}>{item.active ? 'BEREIT' : 'BELEGT'}</span>
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '8px', fontWeight: '700' }}>#{item.internal_number || '---'} | {item.unit_price_net}€ / {item.pricing_type}</div>
                                </div>
                            )})}
                        </div>
                        <div style={{ marginTop: '30px', display: 'flex', gap: '16px' }}>
                            <button className="rental-btn-secondary" style={{ padding: '16px' }} onClick={() => setStep(1)}>⬅ Zurück</button>
                            <button className="rental-btn-primary large" style={{ flex: 1, padding: '16px' }} disabled={rentalItems.length === 0} onClick={() => setStep(3)}>ZEITRAUM WÄHLEN ➔</button>
                        </div>
                    </div>
                )}

                {/* STEP 3: TIMEFRAME & LOCATION */}
                {step === 3 && (
                    <div className="wizard-step">
                        <div style={{ background: 'var(--p-slate-50)', padding: '24px', borderRadius: '16px' }}>
                            
                            {/* Billing Mode Toggle */}
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', background: '#fff', padding: '6px', borderRadius: '12px', border: '1px solid var(--p-slate-200)' }}>
                                <button 
                                    style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: rental.billing_mode === 'days' ? 'var(--p-slate-900)' : 'transparent', color: rental.billing_mode === 'days' ? '#fff' : 'var(--p-slate-500)', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s' }}
                                    onClick={() => setRental({...rental, billing_mode: 'days'})}
                                >
                                    ABRECHNUNG IN TAGEN
                                </button>
                                <button 
                                    style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: rental.billing_mode === 'hours' ? 'var(--p-slate-900)' : 'transparent', color: rental.billing_mode === 'hours' ? '#fff' : 'var(--p-slate-500)', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s' }}
                                    onClick={() => setRental({...rental, billing_mode: 'hours'})}
                                >
                                    ABRECHNUNG IN STUNDEN
                                </button>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: rental.billing_mode === 'hours' ? '1fr 1fr 1fr' : '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                                <div className="form-group-compact">
                                    <label style={{ fontSize: '0.7rem', fontWeight: '900' }}>MIETBEGINN *</label>
                                    <input type="date" value={rental.start_date} onChange={e => setRental({...rental, start_date: e.target.value})} style={{ padding: '12px' }} />
                                </div>
                                <div className="form-group-compact">
                                    <label style={{ fontSize: '0.7rem', fontWeight: '900' }}>GEPLANTE RÜCKGABE</label>
                                    <input type="date" value={rental.end_date} onChange={e => setRental({...rental, end_date: e.target.value})} style={{ padding: '12px' }} />
                                </div>
                                {rental.billing_mode === 'hours' && (
                                    <div className="form-group-compact">
                                        <label style={{ fontSize: '0.7rem', fontWeight: '900', color: 'var(--optimox-blue)' }}>STUNDEN PRO TAG</label>
                                        <input type="number" min="1" max="24" value={rental.hours_per_day} onChange={e => setRental({...rental, hours_per_day: e.target.value})} style={{ padding: '12px', border: '1px solid var(--optimox-blue)', background: 'var(--p-blue-50)' }} />
                                    </div>
                                )}
                            </div>
                            <div className="form-group-compact">
                                <label style={{ fontSize: '0.7rem', fontWeight: '900' }}>EINSATZORT / PROJEKT</label>
                                <input type="text" placeholder="z.B. Baustelle Stadtmitte..." value={rental.location} onChange={e => setRental({...rental, location: e.target.value})} style={{ padding: '12px', background: '#fff' }} />
                            </div>

                            {!isAvailable && (
                                <div style={{ marginTop: '20px', padding: '16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', color: '#dc2626', fontWeight: '800', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '1.2rem' }}>⚠️</span> ACHTUNG: Eines der gewählten Objekte ist in diesem Zeitraum bereits vermietet! Bitte Zeitraum ändern oder Objekt in Schritt 2 abwählen.
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '16px', marginTop: '30px' }}>
                            <button className="rental-btn-secondary" style={{ padding: '16px' }} onClick={() => setStep(2)}>⬅ Zurück</button>
                            <button className="rental-btn-primary large" style={{ flex: 1, padding: '16px' }} disabled={!rental.start_date || !isAvailable} onClick={() => setStep(4)}>KUNDE WÄHLEN ➔</button>
                        </div>
                    </div>
                )}

                {/* STEP 4: CUSTOMER SELECTION */}
                {step === 4 && (
                    <div className="wizard-step">
                        {!showCustomerAdd ? (
                            <div style={{ background: 'var(--p-slate-50)', padding: '24px', borderRadius: '16px' }}>
                                <div className="form-group-compact" style={{ marginBottom: '20px' }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: '900' }}>BESTANDSKUNDEN</label>
                                    <select 
                                        value={rental.customer_id} 
                                        onChange={e => setRental({...rental, customer_id: e.target.value})}
                                        style={{ padding: '14px', fontSize: '0.9rem', borderRadius: '12px' }}
                                    >
                                        <option value="">-- Kunden auswählen --</option>
                                        {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                                    </select>
                                </div>
                                <div style={{ textAlign: 'center', margin: '20px 0', position: 'relative' }}>
                                    <hr style={{ border: 'none', borderTop: '1px solid var(--p-slate-200)' }} />
                                    <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'var(--p-slate-50)', padding: '0 12px', fontSize: '0.65rem', fontWeight: '800', color: 'var(--p-slate-400)' }}>ODER</span>
                                </div>
                                <button className="rental-btn-secondary" style={{ width: '100%', background: '#fff' }} onClick={() => setShowCustomerAdd(true)}>
                                    ➕ NEUEN KUNDEN ANLEGEN
                                </button>
                            </div>
                        ) : (
                            <div style={{ background: 'var(--p-blue-50)', padding: '24px', borderRadius: '16px', border: '1px solid var(--p-blue-100)' }}>
                                <h4 style={{ margin: '0 0 16px 0', fontSize: '0.85rem' }}>NEUEN KUNDEN ERFASSEN</h4>
                                <div className="form-group-compact" style={{ marginBottom: '12px' }}>
                                    <label>Firma / Name*</label>
                                    <input type="text" value={newCustomer.company_name} onChange={e => setNewCustomer({...newCustomer, company_name: e.target.value})} />
                                </div>
                                <div className="form-row-multi" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div className="form-group-compact">
                                        <label>Ansprechpartner</label>
                                        <input type="text" value={newCustomer.contact_person} onChange={e => setNewCustomer({...newCustomer, contact_person: e.target.value})} />
                                    </div>
                                    <div className="form-group-compact">
                                        <label>E-Mail</label>
                                        <input type="email" value={newCustomer.email} onChange={e => setNewCustomer({...newCustomer, email: e.target.value})} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                                    <button className="rental-btn-primary" onClick={handleAddCustomer}>Speichern & Auswählen</button>
                                    <button className="rental-btn-secondary" onClick={() => setShowCustomerAdd(false)}>Abbrechen</button>
                                </div>
                            </div>
                        )}
                        {!showCustomerAdd && (
                            <div style={{ display: 'flex', gap: '16px', marginTop: '30px' }}>
                                <button className="rental-btn-secondary" style={{ padding: '16px' }} onClick={() => setStep(3)}>⬅ Zurück</button>
                                <button className="rental-btn-primary large" style={{ flex: 1, padding: '16px' }} disabled={!rental.customer_id} onClick={() => setStep(5)}>ZUSTAND BESTIMMEN ➔</button>
                            </div>
                        )}
                    </div>
                )}

                {/* STEP 5: CONDITION & PHOTOS (SIMPLIFIED) */}
                {step === 5 && (
                    <div className="wizard-step">
                        <div style={{ background: 'var(--p-slate-50)', padding: '24px', borderRadius: '16px' }}>
                            <div className="form-group-compact" style={{ marginBottom: '24px' }}>
                                <label style={{ fontSize: '0.75rem', fontWeight: '900' }}>ZUSTAND / MÄNGEL BEI ÜBERGABE</label>
                                <textarea 
                                    rows="4" 
                                    placeholder="Sauber, vollgetankt, keine neuen Schäden..." 
                                    value={rental.handover_condition || ''} 
                                    onChange={e => setRental({...rental, handover_condition: e.target.value})}
                                    style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid var(--p-slate-200)', fontSize: '0.9rem', marginTop: '8px' }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div style={{ 
                                    padding: '24px', 
                                    background: '#fff', 
                                    borderRadius: '16px', 
                                    border: '2px dashed var(--p-slate-200)', 
                                    textAlign: 'center',
                                    cursor: 'pointer'
                                }}>
                                    <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>📸</div>
                                    <div style={{ fontSize: '0.75rem', fontWeight: '800' }}>FOTOS HINZUFÜGEN</div>
                                    <div style={{ fontSize: '0.55rem', color: 'var(--p-slate-400)', marginTop: '4px' }}>Max. 10 Bilder (.jpg, .png)</div>
                                </div>
                                <div style={{ 
                                    padding: '24px', 
                                    background: '#fff', 
                                    borderRadius: '16px', 
                                    border: '2px dashed var(--p-slate-200)', 
                                    textAlign: 'center',
                                    cursor: 'pointer'
                                }}>
                                    <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>📄</div>
                                    <div style={{ fontSize: '0.75rem', fontWeight: '800' }}>DOKUMENT ANHÄNGEN</div>
                                    <div style={{ fontSize: '0.55rem', color: 'var(--p-slate-400)', marginTop: '4px' }}>z.B. Protokolle (.pdf)</div>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '16px', marginTop: '30px' }}>
                            <button className="rental-btn-secondary" style={{ padding: '16px' }} onClick={() => setStep(4)}>⬅ Zurück</button>
                            <button className="rental-btn-primary large" style={{ flex: 1, padding: '16px' }} onClick={() => setStep(6)}>RECHNUNGSDATEN ➔</button>
                        </div>
                    </div>
                )}

                {/* STEP 6: RECHNUNG / PRICING */}
                {step === 6 && (
                    <div className="wizard-step">
                        <div className="premium-card" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--p-slate-100)' }}>
                            <table className="premium-style-table">
                                <thead style={{ background: 'var(--p-slate-50)' }}>
                                    <tr>
                                        <th style={{ paddingLeft: '20px' }}>POSITION</th>
                                        <th style={{ width: '120px' }}>ABRECHNUNG</th>
                                        <th style={{ width: '120px', textAlign: 'right' }}>PREIS NETTO</th>
                                        <th style={{ width: '50px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rentalItems.map((item, idx) => (
                                        <tr key={idx}>
                                            <td style={{ paddingLeft: '20px' }}>
                                                {item.is_inventory ? (
                                                    <div style={{ fontWeight: '800' }}>{item.name}</div>
                                                ) : (
                                                    <input 
                                                        type="text" 
                                                        value={item.name} 
                                                        onChange={e => updateItem(idx, 'name', e.target.value)}
                                                        style={{ border: 'none', background: 'transparent', width: '100%', fontWeight: '600' }}
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                <select value={item.pricing_type} onChange={e => updateItem(idx, 'pricing_type', e.target.value)} style={{ fontSize: '0.75rem', width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--p-slate-200)' }}>
                                                    <option value="day">pro Tag</option>
                                                    <option value="hour">pro Std.</option>
                                                    <option value="flat">pauschal</option>
                                                </select>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                                                    <input 
                                                        type="number" 
                                                        step="0.01" 
                                                        value={item.unit_price_net} 
                                                        onChange={e => updateItem(idx, 'unit_price_net', e.target.value)} 
                                                        style={{ width: '80px', textAlign: 'right', fontWeight: '800' }} 
                                                    />
                                                    <span style={{ fontSize: '0.7rem' }}>€</span>
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <button onClick={() => setRentalItems(rentalItems.filter((_, i) => i !== idx))} style={{ color: '#e11d48', border: 'none', background: 'none', cursor: 'pointer' }}>✕</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                            <button className="rental-btn-secondary" style={{ border: '1px dashed var(--p-slate-300)' }} onClick={handleAddManualItem}>
                                ➕ ZUSATZKOSTEN / SERVICE HINZUFÜGEN
                            </button>
                            <div style={{ background: '#fff', padding: '12px 20px', borderRadius: '12px', border: '1px solid var(--p-slate-200)', display: 'flex', gap: '16px', alignItems: 'center' }}>
                                <strong style={{ fontSize: '0.8rem', color: 'var(--p-slate-700)' }}>STEUERSATZ:</strong>
                                <select value={rental.vat_rate} onChange={e => setRental({...rental, vat_rate: e.target.value})} style={{ padding: '6px 12px', fontSize: '0.85rem', borderRadius: '6px', border: '1px solid var(--p-slate-300)', fontWeight: '700' }}>
                                    <option value="19">19% (Standard)</option>
                                    <option value="7">7% (Ermäßigt)</option>
                                    <option value="0">0% (z.B. Reverse Charge)</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '16px', marginTop: '30px' }}>
                            <button className="rental-btn-secondary" style={{ padding: '16px' }} onClick={() => setStep(5)}>⬅ Zurück</button>
                            <button className="rental-btn-primary large" style={{ flex: 1, padding: '16px' }} onClick={() => setStep(7)}>ABSCHLUSS ➔</button>
                        </div>
                    </div>
                )}

                {/* STEP 7: SUMMARY */}
                {step === 7 && (
                    <div className="wizard-step">
                        <div style={{ background: 'var(--p-slate-50)', padding: '30px', borderRadius: '20px', border: '1px solid var(--p-slate-200)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
                                <div>
                                    <div style={{ fontSize: '0.65rem', fontWeight: '900', color: 'var(--p-slate-400)' }}>MIETER</div>
                                    <div style={{ fontSize: '1rem', fontWeight: '800' }}>{currentCustomer?.company_name}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.65rem', fontWeight: '900', color: 'var(--p-slate-400)' }}>VORGANG</div>
                                    <div style={{ fontSize: '1rem', fontWeight: '900', color: 'var(--optimox-blue)' }}>#{rental.rental_number}</div>
                                </div>
                            </div>
                            <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid var(--p-slate-100)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--p-slate-100)', color: 'var(--p-slate-500)' }}>
                                            <th style={{ textAlign: 'left', paddingBottom: '10px' }}>Position</th>
                                            <th style={{ textAlign: 'center', paddingBottom: '10px' }}>Menge / Dauer</th>
                                            <th style={{ textAlign: 'right', paddingBottom: '10px' }}>Einzel Netto</th>
                                            <th style={{ textAlign: 'right', paddingBottom: '10px' }}>Gesamt Netto</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rentalItems.map((item, i) => {
                                            const { net, multiplier } = calculateItemTotals(item);
                                            let durationText = '';
                                            if (item.pricing_type === 'day') durationText = `${multiplier} Tag(e)`;
                                            if (item.pricing_type === 'hour') durationText = `${multiplier} Std.`;
                                            if (item.pricing_type === 'flat') durationText = `1 Pauschal`;

                                            return (
                                                <tr key={i} style={{ borderBottom: i < rentalItems.length - 1 ? '1px solid var(--p-slate-50)' : 'none' }}>
                                                    <td style={{ padding: '12px 0', fontWeight: '700', color: 'var(--p-slate-800)' }}>{item.name}</td>
                                                    <td style={{ padding: '12px 0', textAlign: 'center', color: 'var(--p-slate-600)' }}>{durationText}</td>
                                                    <td style={{ padding: '12px 0', textAlign: 'right', color: 'var(--p-slate-600)' }}>{parseFloat(item.unit_price_net).toFixed(2)} €</td>
                                                    <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: '800', color: 'var(--p-slate-900)' }}>{net.toFixed(2)} €</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Totals Block */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingTop: '15px', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '300px', fontSize: '0.85rem', color: 'var(--p-slate-600)' }}>
                                    <span>Gesamtsumme Netto:</span>
                                    <span style={{ fontWeight: '700' }}>{calculateGrandTotals().totalNet.toFixed(2)} €</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '300px', fontSize: '0.85rem', color: 'var(--p-slate-600)' }}>
                                    <span>zzgl. {rental.vat_rate}% MwSt.:</span>
                                    <span style={{ fontWeight: '700' }}>{calculateGrandTotals().totalVat.toFixed(2)} €</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '300px', fontSize: '1.1rem', color: 'var(--optimox-blue)', marginTop: '8px', paddingTop: '12px', borderTop: '2px solid var(--p-slate-200)' }}>
                                    <span style={{ fontWeight: '900' }}>GESAMTBETRAG BRUTTO:</span>
                                    <span style={{ fontWeight: '900' }}>{calculateGrandTotals().totalBrutto.toFixed(2)} €</span>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '16px', marginTop: '30px' }}>
                            <button className="rental-btn-secondary" style={{ padding: '16px' }} onClick={() => setStep(6)}>⬅ Zurück</button>
                            <button className="rental-btn-primary large" style={{ flex: 1, padding: '16px', background: '#10b981' }} disabled={submitting} onClick={handleCreate}>
                                {submitting ? 'SPEICHERT...' : '✅ VERMIETUNG JETZT STARTEN'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
